<script setup lang="ts">
import ControlCurrentRoute from '@/components/shared/admin/reports/ControlCurrentRoute.vue'
import Graphics from '@/components/shared/admin/reports/Graphics.vue'
import TotalCard from '@/components/shared/admin/reports/TotalCard.vue'
import WorkshopSelector from '@/components/shared/admin/reports/WorkshopSelector.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import { useDownloadReport } from '@/composables/useDownloadReport'
import { formatPrice } from '@/helpers/current-price'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head } from '@inertiajs/vue3'
import { useUrlSearchParams } from '@vueuse/core'
import { FileText, Loader2 } from 'lucide-vue-next'

interface Report {
    report: {
        name: string
        total: number
        value: number
    }[]
    total: number
}

interface Workshop {
    id: number
    description: string
    start: string
}

interface CurrentWorkshop {
    id: number
    description: string
}

const props = defineProps<{
    workshops: Workshop[]
    current_workshop: CurrentWorkshop | null
    total?: Report
    pix?: Report
    bank_slip?: Report
    company_billing?: Report
    credit_card?: Report
    paypal?: Report
}>()

const colors = ['#2DD4BF', '#FDBA74']

const currentRoute = 'sellers'

const { download_pdf: downloadPdf } = useUrlSearchParams()

const { handleDownload, isGenerating } = useDownloadReport('sellers', props.current_workshop?.id)
</script>

<template>
    <Head title="Relatórios" />
    <div class="bg-slate-50">
        <div class="grid grid-cols-5"></div>
        <LightLayout :downloadPdf="String(downloadPdf)">
            <div class="container">
                <Title v-if="!downloadPdf">Relatórios</Title>
                <div v-if="!downloadPdf" class="mt-4 flex items-center justify-between gap-4">
                    <div class="flex items-center gap-4">
                        <ControlCurrentRoute :current-route="currentRoute" :current-workshop-id="current_workshop?.id ?? null" />
                        <WorkshopSelector :workshops="workshops" :current-workshop-id="current_workshop?.id ?? null" />
                    </div>

                    <div v-if="current_workshop">
                        <Button variant="outline" class="gap-5 rounded-md bg-white" @click="handleDownload" :disabled="isGenerating">
                            <template v-if="!isGenerating"> PDF <FileText class="text-slate-400" /> </template>
                            <template v-else>
                                <Loader2 class="animate-spin" />
                            </template>
                        </Button>
                    </div>
                </div>

                <template v-if="current_workshop">
                    <Title class="mt-6" size="sm">Relatório de vendas</Title>

                    <Card class="mt-4">
                        <CardContent class="grid gap-y-4 p-2 lg:grid-cols-2 lg:p-6">
                            <div class="border-slate-200 px-4 lg:border-r lg:px-8">
                                <Graphics sublabel="Total" title="Total de Vendas" :data="total!.report" :colors="colors">
                                    <template #total-cards>
                                        <TotalCard
                                            v-for="item in total!.report"
                                            :key="item.name"
                                            :title="`Valor ${item.name.toLocaleLowerCase()}`"
                                            :value="formatPrice(item.value / 100)"
                                        />

                                        <TotalCard title="Total" :value="formatPrice(total!.total / 100)" class="text-center font-semibold" />
                                    </template>
                                </Graphics>
                            </div>
                            <div class="">
                                <Graphics sublabel="Total" title="Total Faturamento CNPJ" :data="company_billing!.report" :colors="colors">
                                    <template #total-cards>
                                        <TotalCard
                                            v-for="item in company_billing!.report"
                                            :key="item.name"
                                            :title="`Valor ${item.name.toLocaleLowerCase()}`"
                                            :value="formatPrice(item.value / 100)"
                                        />

                                        <TotalCard
                                            title="Total"
                                            :value="formatPrice(company_billing!.total / 100)"
                                            class="text-center font-semibold"
                                        />
                                    </template>
                                </Graphics>
                            </div>
                        </CardContent>
                    </Card>
                    <Card class="mt-4" :class="{ 'break-page': downloadPdf }">
                        <CardContent class="grid gap-y-4 p-6 lg:grid-cols-2">
                            <div class="border-slate-200 px-4 lg:border-r lg:px-8">
                                <Graphics sublabel="Total" title="Total de PIX" :data="pix!.report" :colors="colors">
                                    <template #total-cards>
                                        <TotalCard
                                            v-for="item in pix!.report"
                                            :key="item.name"
                                            :title="`Valor ${item.name.toLocaleLowerCase()}`"
                                            :value="formatPrice(item.value / 100)"
                                        />

                                        <TotalCard title="Total" :value="formatPrice(pix!.total / 100)" class="text-center font-semibold" />
                                    </template>
                                </Graphics>
                            </div>
                            <div class="px-4 lg:px-8">
                                <Graphics sublabel="Total" title="Total Boleto" :data="bank_slip!.report" :colors="colors">
                                    <template #total-cards>
                                        <TotalCard
                                            v-for="item in bank_slip!.report"
                                            :key="item.name"
                                            :title="`Valor ${item.name.toLocaleLowerCase()}`"
                                            :value="formatPrice(item.value / 100)"
                                        />

                                        <TotalCard title="Total" :value="formatPrice(bank_slip!.total / 100)" class="text-center font-semibold" />
                                    </template>
                                </Graphics>
                            </div>
                        </CardContent>
                    </Card>
                    <Card class="mt-4" :class="{ 'break-page': downloadPdf }">
                        <CardContent class="grid gap-y-4 p-6 lg:grid-cols-2">
                            <div class="border-slate-200 px-4 lg:border-r lg:px-8">
                                <Graphics sublabel="Total" title="Total Cartão de Crédito" :data="credit_card!.report" :colors="colors">
                                    <template #total-cards>
                                        <TotalCard
                                            v-for="item in credit_card!.report"
                                            :key="item.name"
                                            :title="`Valor ${item.name.toLocaleLowerCase()}`"
                                            :value="formatPrice(item.value / 100)"
                                        />
                                        <TotalCard title="Total" :value="formatPrice(credit_card!.total / 100)" class="text-center font-semibold" />
                                    </template>
                                </Graphics>
                            </div>
                            <div class="px-4 lg:px-8">
                                <Graphics sublabel="Total" title="Total PayPal" :data="paypal!.report" :colors="colors">
                                    <template #total-cards>
                                        <TotalCard
                                            v-for="item in paypal!.report"
                                            :key="item.name"
                                            :title="`Valor ${item.name.toLocaleLowerCase()}`"
                                            :value="formatPrice(item.value / 100)"
                                        />

                                        <TotalCard title="Total" :value="formatPrice(paypal!.total / 100)" class="text-center font-semibold" />
                                    </template>
                                </Graphics>
                            </div>
                        </CardContent>
                    </Card>
                </template>

                <Card v-else class="mt-6">
                    <CardContent class="p-6 text-center text-slate-500"> Selecione um evento para visualizar o relatório de vendas. </CardContent>
                </Card>
            </div>
        </LightLayout>
    </div>
</template>
